import type { ShortcutAction } from "../types";

/** Combinação default de cada ação remapeável - usada quando a pessoa não customizou nada. */
export const DEFAULT_SHORTCUTS: Record<ShortcutAction, string> = {
  search: "mod+k",
  newConversation: "mod+j",
  shortcutsHelp: "?",
  toggleFocusMode: "mod+.",
  commandPalette: "mod+shift+k",
};

export const SHORTCUT_LABEL: Record<ShortcutAction, string> = {
  search: "Buscar nas conversas",
  newConversation: "Nova conversa",
  shortcutsHelp: "Abrir ajuda de atalhos",
  toggleFocusMode: "Ligar/desligar modo de apresentação",
  commandPalette: "Abrir paleta de comandos",
};

/**
 * Serializa a combinação de um evento de teclado - "mod" cobre Ctrl/Cmd
 * (Mac/Windows/Linux não precisam de combos diferentes guardados). Shift só
 * entra explícito pra teclas não-imprimíveis (Tab, setas…), já que teclas
 * imprimíveis (letra, símbolo) já reflectem o shift no próprio `e.key`.
 */
export function comboFromEvent(e: KeyboardEvent): string {
  const key = e.key;
  if (["Control", "Meta", "Shift", "Alt"].includes(key)) return "";
  const printable = key.length === 1;
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push("mod");
  if (e.altKey) parts.push("alt");
  if (!printable && e.shiftKey) parts.push("shift");
  parts.push(key.toLowerCase());
  return parts.join("+");
}

export function matchShortcut(e: KeyboardEvent, combo: string): boolean {
  return Boolean(combo) && comboFromEvent(e) === combo;
}

/** Combinação efetiva de uma ação: a customizada, se houver, senão o default. */
export function effectiveShortcut(
  shortcuts: Partial<Record<ShortcutAction, string>> | undefined,
  action: ShortcutAction,
): string {
  return shortcuts?.[action] || DEFAULT_SHORTCUTS[action];
}

/** Rótulo amigável da combinação pra exibir na UI (ex.: "mod+k" -> "⌘K" ou "Ctrl+K"). */
export function formatCombo(combo: string): string {
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return combo
    .split("+")
    .map((part) => {
      if (part === "mod") return isMac ? "⌘" : "Ctrl";
      if (part === "shift") return isMac ? "⇧" : "Shift";
      if (part === "alt") return isMac ? "⌥" : "Alt";
      return part.length === 1 ? part.toUpperCase() : part;
    })
    .join(isMac ? "" : "+");
}
